import { Badge, BadgeAction } from '@/components/ui/badge'
import { EmptyState } from '@/components/patterns/empty-state'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  useCreateStockImageTagMutation,
  useDeleteStockImageTagMutation,
  useStockImageTags,
  useUpdateStockImageTagMutation,
} from '@/lib/services/stock-images/stock-images.query'
import { stockImageTagSchema } from '@/lib/services/stock-images/stock-images.schema'
import { getErrorMessage } from '@/lib/utils'
import { Archive, Check, ChevronDown, Pencil, Plus, Settings2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

function getTagIdSet(tagIds = []) {
  return new Set(tagIds.map((tagId) => Number(tagId)).filter((tagId) => tagId > 0))
}

const builtInUsageTagKeys = new Set([
  'stock_usage_product',
  'stock_usage_banner',
  'stock_usage_visit_our_shop',
])

function isBuiltInUsageTag(tag) {
  return builtInUsageTagKeys.has(tag?.key)
}

export function StockTagManager({ disabled = false, selectedTagIds = [], onSelectedTagIdsChange }) {
  const { data: tagsResponse, isLoading } = useStockImageTags()
  const tags = tagsResponse?.data ?? []
  const selectedSet = getTagIdSet(selectedTagIds)
  const selectedTags = tags.filter((tag) => selectedSet.has(tag.id))
  const availableTags = tags.filter((tag) => !selectedSet.has(tag.id))

  const addTag = (tagId) => {
    if (disabled) {
      return
    }

    onSelectedTagIdsChange?.([...new Set([...selectedTagIds, Number(tagId)])])
  }

  const removeTag = (tagId) => {
    if (disabled) {
      return
    }

    onSelectedTagIdsChange?.(selectedTagIds.filter((selectedTagId) => selectedTagId !== tagId))
  }

  return (
    <section className="space-y-4 rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <Typography variant="h4" className="pb-0">
            Tags
          </Typography>
          <Typography variant="supporting" className="block">
            Add or remove the tags florists use to filter this stock image.
          </Typography>
        </div>

        {!disabled ? <StockTagManagementDialog onArchivedTag={removeTag} /> : null}
      </div>

      <div className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="justify-between sm:min-w-64"
                disabled={disabled || isLoading || availableTags.length === 0}
              >
                <span>{isLoading ? 'Loading tags...' : 'Add tag'}</span>
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" size="xl">
              <DropdownMenuLabel>Available tags</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableTags.map((tag) => (
                <DropdownMenuItem key={tag.id} onSelect={() => addTag(tag.id)}>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{tag.label}</span>
                    {tag.description ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {tag.description}
                      </span>
                    ) : null}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!isLoading && availableTags.length === 0 ? (
            <Typography variant="supporting" className="block">
              {tags.length === 0 ? 'No tags created yet.' : 'All tags are already selected.'}
            </Typography>
          ) : null}
        </div>

        <div className="min-h-12 rounded-lg border bg-muted/20 p-3">
          {selectedTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <Badge key={tag.id} variant="secondary" className="gap-1.5 py-1 pr-1">
                  <span className="max-w-44 truncate">{tag.label}</span>
                  {!disabled ? (
                    <BadgeAction
                      aria-label={`Remove ${tag.label}`}
                      onClick={() => removeTag(tag.id)}
                    >
                      <X className="size-3" />
                    </BadgeAction>
                  ) : null}
                </Badge>
              ))}
            </div>
          ) : (
            <Typography variant="supporting" className="block">
              No tags selected.
            </Typography>
          )}
        </div>
      </div>
    </section>
  )
}

function StockTagManagementDialog({ onArchivedTag }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Settings2 className="size-4" />
          Manage Tags
        </Button>
      </DialogTrigger>
      <DialogContent size="xlarge" viewport="scroll" className="max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
          <DialogDescription>
            Create, edit, and archive the global tags available for stock images.
          </DialogDescription>
        </DialogHeader>
        <StockTagCrud onArchivedTag={onArchivedTag} />
      </DialogContent>
    </Dialog>
  )
}

function StockTagCrud({ onArchivedTag }) {
  const { data: tagsResponse, isLoading } = useStockImageTags()
  const tags = tagsResponse?.data ?? []
  const createTagMutation = useCreateStockImageTagMutation()
  const updateTagMutation = useUpdateStockImageTagMutation()
  const deleteTagMutation = useDeleteStockImageTagMutation()
  const [newTagLabel, setNewTagLabel] = useState('')
  const [newTagDescription, setNewTagDescription] = useState('')
  const [editingTagId, setEditingTagId] = useState(null)
  const [editingTagLabel, setEditingTagLabel] = useState('')
  const [editingTagDescription, setEditingTagDescription] = useState('')

  const handleCreateTag = async (event) => {
    event?.preventDefault()

    const parsed = stockImageTagSchema.safeParse({
      label: newTagLabel,
      description: newTagDescription,
    })

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Enter a tag name')
      return
    }

    try {
      await createTagMutation.mutateAsync(parsed.data)
      setNewTagLabel('')
      setNewTagDescription('')
      toast.success('Stock image tag created')
    } catch (error) {
      toast.error('Failed to create stock image tag', {
        description: getErrorMessage(error),
      })
    }
  }

  const startEditingTag = (tag) => {
    setEditingTagId(tag.id)
    setEditingTagLabel(tag.label ?? '')
    setEditingTagDescription(tag.description ?? '')
  }

  const stopEditingTag = () => {
    setEditingTagId(null)
    setEditingTagLabel('')
    setEditingTagDescription('')
  }

  const handleUpdateTag = async (event, tagId) => {
    event?.preventDefault()

    const parsed = stockImageTagSchema.safeParse({
      label: editingTagLabel,
      description: editingTagDescription,
    })

    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Enter a tag name')
      return
    }

    try {
      await updateTagMutation.mutateAsync({
        tagId,
        values: parsed.data,
      })
      stopEditingTag()
      toast.success('Stock image tag saved')
    } catch (error) {
      toast.error('Failed to save stock image tag', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleArchiveTag = async (tagId) => {
    try {
      await deleteTagMutation.mutateAsync(tagId)
      onArchivedTag?.(tagId)
      toast.success('Stock image tag archived')
    } catch (error) {
      toast.error('Failed to archive stock image tag', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-lg border bg-muted/20 p-3">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="stock-tag-label">New Tag</Label>
            <Input
              id="stock-tag-label"
              value={newTagLabel}
              placeholder="Baby's Breath"
              onChange={(event) => setNewTagLabel(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stock-tag-description">Description</Label>
            <Input
              id="stock-tag-description"
              value={newTagDescription}
              placeholder="Optional internal note"
              onChange={(event) => setNewTagDescription(event.target.value)}
            />
          </div>
          <Button type="button" disabled={createTagMutation.isPending} onClick={handleCreateTag}>
            <Plus className="size-4" />
            Add Tag
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Typography variant="supporting" className="block">
          Loading tags...
        </Typography>
      ) : tags.length === 0 ? (
        <EmptyState size="compact" title="No stock image tags yet." />
      ) : (
        <div className="grid gap-2">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="flex flex-col gap-3 rounded-lg border bg-background p-3 sm:flex-row sm:items-start sm:justify-between"
            >
              {editingTagId === tag.id && !isBuiltInUsageTag(tag) ? (
                <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto]">
                  <div className="space-y-2">
                    <Label htmlFor={`stock-tag-edit-label-${tag.id}`}>Tag</Label>
                    <Input
                      id={`stock-tag-edit-label-${tag.id}`}
                      value={editingTagLabel}
                      onChange={(event) => setEditingTagLabel(event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`stock-tag-edit-description-${tag.id}`}>Description</Label>
                    <Input
                      id={`stock-tag-edit-description-${tag.id}`}
                      value={editingTagDescription}
                      onChange={(event) => setEditingTagDescription(event.target.value)}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={updateTagMutation.isPending}
                      onClick={(event) => handleUpdateTag(event, tag.id)}
                    >
                      <Check className="size-4" />
                      Save
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={stopEditingTag}>
                      <X className="size-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{tag.label}</span>
                      {isBuiltInUsageTag(tag) ? <Badge variant="outline">Built-in</Badge> : null}
                    </div>
                    {tag.description ? (
                      <Typography variant="caption" className="block">
                        {tag.description}
                      </Typography>
                    ) : null}
                  </div>
                  {!isBuiltInUsageTag(tag) ? (
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="secondary"
                            size="icon-xs"
                            aria-label={`Edit ${tag.label}`}
                            disabled={updateTagMutation.isPending}
                            onClick={() => startEditingTag(tag)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outlineDestructive"
                            size="icon-xs"
                            aria-label={`Archive ${tag.label}`}
                            disabled={deleteTagMutation.isPending}
                            onClick={() => handleArchiveTag(tag.id)}
                          >
                            <Archive className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent variant="destructive">Archive</TooltipContent>
                      </Tooltip>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
